import type { AnchorHTMLAttributes, ReactNode } from 'react'

/*
  Primitives in the Magic Lamp system: 3px corners, 1px warm-grey rules, Geist
  headings, Geist Mono uppercase labels. Amber marks only what the lamp is doing.
*/

export type ActionVariant = 'solid' | 'outline' | 'light'

/** Mono uppercase action, 3px corners. Solid is near-black; light sits on dark panels. */
export function actionClass(variant: ActionVariant = 'solid', size: 'sm' | 'lg' = 'sm', className = ''): string {
  const base =
    'mono-label inline-flex items-center justify-center gap-1.5 rounded-[3px] whitespace-nowrap no-underline transition-colors duration-150 disabled:pointer-events-none disabled:opacity-40'
  const height = size === 'lg' ? 'h-10 px-4' : 'h-7 px-3'
  const look =
    variant === 'solid'
      ? 'bg-dark text-canvas hover:bg-dark-2'
      : variant === 'outline'
        ? 'border border-rule-strong bg-canvas text-ink hover:bg-panel'
        : 'bg-canvas text-ink hover:bg-white'
  return `${base} ${height} ${look} ${className}`
}

interface ButtonLinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  variant?: ActionVariant
  size?: 'sm' | 'lg'
  children: ReactNode
}

export function ButtonLink({ variant = 'solid', size = 'lg', className = '', children, ...rest }: ButtonLinkProps) {
  return (
    <a className={actionClass(variant, size, className)} {...rest}>
      {children}
    </a>
  )
}

/** Surfaces, so every section draws cards, panels and tables the same way. */
export const card = 'rounded-[3px] border border-rule bg-card'
export const panel = 'rounded-[3px] bg-panel'
export const darkPanel = 'grain relative overflow-hidden rounded-[3px] bg-dark text-on-dark'
export const tableWrap = 'overflow-x-auto rounded-[3px] border border-rule bg-card'
export const th = 'mono-label py-3 pr-4 font-normal text-ink-3 first:pl-5 last:pr-5'
export const tr = 'border-t border-dashed border-rule'
export const td = 'py-3 pr-4 align-middle first:pl-5 last:pr-5'

export function DownloadIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  )
}

/** The Genie symbol. Inherits currentColor. */
export function GenieMark({ className = 'size-5' }: { className?: string }) {
  return (
    <svg viewBox="0 0 1024 1024" fill="currentColor" className={className} aria-hidden>
      <path d="M794.06,933.07c-13.47-.65-32.63-.93-39.36,10.05-3.61,5.88-5.21,12.78-5.18,19.71l.1,20.37c.04,7.42-.3,14.51-2.84,21.51-1.48,4.08-3.48,8.49-7.14,11.09-6.35,4.52-13.9,7.48-22.18,7.49l-121.79.18-286.17.08c-8.05,0-15.39-1.93-22.35-5.13-11.6-5.35-14.2-22.94-13.92-35.93.31-14.56,1.76-35.7-9.03-43.59-5.57-4.07-12.8-5.84-19.71-5.83l-23.9.03c-13.48.02-30.18-1.5-36.54-13.74-2.86-5.5-4.88-11.91-4.94-18.22l-.22-21.63-.09-101.86c-.01-11.11-4.57-22.53-15.13-26.01-3.67-1.21-7.89-2.42-11.8-2.41l-20.43.07c-14.55.05-31.23-1.83-37.38-15.08-2.97-6.4-4.63-13.3-4.67-20.52l-.12-27.43c-.02-4.21-1.34-8.55-2.84-12.46-3.85-10.05-15.19-14.12-25.85-14.09l-20.38.04c-7.36.02-14.21-.49-21.13-2.89-10.45-3.63-17.38-12.39-18.42-23.69l-.75-28.4.21-25.14c.06-6.94,1.79-13.72,4.82-19.77,5.03-10.04,19.05-13.94,30.36-13.93l43.51.06c6.74,0,13.34,1.94,19.33,4.44,14.35,5.99,16.37,25.94,15.63,41.15-.88,18.19-.65,38.01,18.23,42.41,11.71,2.73,31.95,2.4,39.53-5.87,4.35-4.75,7.13-11.71,7.17-18.25l.22-35.48.12-87.32c0-6.91,1.56-13.27,3.88-19.46,4.71-12.52,20.25-16.27,33.47-16.3l29.52-.07c7.06-.02,15.06-2.24,19.98-7.34,6.17-6.39,7.45-15.72,7.41-24.22l-.09-18.31c-.05-10.72,2.17-26.49,10.13-31.99,7.35-5.08,15.87-7.44,25.09-7.45l116.99-.05c5.45,0,10.39-1.66,15.16-3.73,10.6-4.6,12.23-17.87,12.51-29.07.21-8.19-.65-16.11-5.08-23.18-4.76-7.59-15.85-10.14-25.25-10.17l-27.22-.08c-6.28-.02-12.62-2.16-18.34-4.5-3.98-1.63-7.16-4.74-9.09-8.48-2.91-5.65-5.19-11.9-5.28-18.3l-.23-16.82v-124.03c0-7.2,1.96-13.63,4.43-20.07,4.09-10.65,19.3-14.92,31.2-14.93l64.53-.07,52.69-.16c4.46-.01,9.1-1.39,13.1-2.88,5.42-2.01,9.23-6.16,11.08-11.53,1.22-3.56,2.7-7.62,2.72-11.44l.17-28.54c.04-7.12,1.78-14.12,4.53-20.56,2.16-5.05,5.99-8.75,10.94-10.83,1.75-.74,3.16-1.69,4.92-2.95h349.41s3.81,1.81,3.81,1.81c27.46,13.11,13.99,44.09,19.88,69.13,2.08,8.86,8.51,14.73,17.12,16.69.75,1.3,2.35,2.75,3.75,2.75l45.63-.02c7.64,2.43,13.92,7.79,17.09,15.25l.02,80.75c-2.34,5.03-5.88,9.32-11.01,11.63-6.19,2.79-13.08,4.62-20.05,4.63h-42.81c-11.04,0-25.48-3.92-30.47-13.86-3.29-6.54-5.08-13.87-5.11-21.31l-.11-26.06c-.03-7.13-1.53-14.95-6.25-20.6-4.18-4.99-11.55-8.29-18.5-8.31l-102.86-.25c-13.05-.03-26.12,2.39-30.38,15.38-1.44,4.41-2.53,9.05-2.51,13.8l.11,20.37c.07,12.85-1.32,28.98-12.94,35.26-6.48,3.5-13.74,5.49-21.21,5.56l-28.55.24c-6.82.06-14.44,2.35-19.35,7.24s-7.18,12.52-7.23,19.32l-.24,31.36c-.08,10.93-3.75,25.3-13.42,30.14-6.11,3.06-12.96,5.15-19.83,5.21l-34.97.29c-6.51.05-13.94,2.3-18.52,6.78-6.71,6.55-7.77,15.9-7.79,25-.02,9.17.43,19.32,7.04,26.37,5.58,5.95,14.67,7.8,22.86,7.8l119.2.03c10.43,0,24.65,4.01,29.5,12.83,3.42,6.21,5.28,13.13,5.38,20.28l.47,31.55c.09,6.02,2.13,13.01,6.53,17.24,6.09,5.85,15.18,7.6,23.44,7.57l19.74-.08c7.48-.03,14.93.87,21.85,3.28,13.61,4.75,18.28,18.26,18.29,32.52l.05,118.96c0,5.99,1.38,11.44,3.67,16.68,4.53,10.37,17.66,12.81,29.14,12.72s26.36-.63,31.99-9.82c3.06-5,5.24-11.19,5.26-17.17l.08-23.93c.04-12.43,2.6-27.7,13.45-33.39,8.28-4.34,17.1-5.43,26.43-5.36l39.32.28c7.95.06,15.8,2.18,22.46,6.19,4.9,2.96,7.68,8.3,9.52,13.62,2.46,7.13,2.68,14.52,2.67,22.11l-.06,30.23c-.02,9.02-1.03,17.65-4.52,25.87-6.39,15.1-27.36,16.26-43.44,15.58-12.31-.52-29.56-.29-36.08,9.44-4.4,6.57-5.66,14.3-5.6,22.08l.12,16.17c.13,16.92-2.54,34.83-20.24,39.23-4.35,1.08-8.97,2.48-13.45,2.49l-30.74.11c-9.67.03-21.6,2.7-25.65,11.16-2.39,5-4.34,10.13-4.34,15.94l-.05,120.46c0,12.78-3.9,27.36-15.69,32.18-9.69,3.95-19.68,4.63-30.1,4.13ZM358.34,714.56c3.09-5.55,4.27-11.37,4.27-17.71v-28.11s.02-65.92.02-65.92c0-8.88-.77-18.77-7.3-25.24-6.5-6.44-16.39-7.43-25.23-7.43s-18.3.85-24.91,7.15c-5.7,5.43-7.57,14.12-7.56,22.05l.03,97.43c0,5.3,1.09,10.21,2.71,14.98,3.5,10.31,15.23,13.72,26.22,14.13,12.11.46,25.98-.95,31.75-11.33ZM722.22,714.07c2.34-4.72,3.94-9.62,3.94-15.19v-100.91c0-4.98-1.4-9.61-3.06-13.95-4.48-11.71-17.79-13.82-30.19-13.87-8.62-.03-18.32.7-25.02,6.99-4.88,4.58-7.61,12.63-7.62,19.55l-.09,99.44c0,7.79,1.63,16.09,6.76,21.85,5.58,6.28,14.54,7.53,22.48,7.89,12.4.56,27.4-.92,32.79-11.8Z" />
    </svg>
  )
}


/** Section opener: eyebrow, a 36px title, an optional note on the right. */
export function SectionHead({
  eyebrow,
  title,
  note,
  id,
}: {
  eyebrow: string
  title: ReactNode
  note?: ReactNode
  id?: string
}) {
  return (
    <div className="mb-8">
      <span className="eyebrow">{eyebrow}</span>
      <div className="mt-4 flex flex-wrap items-end justify-between gap-6">
        <h2 id={id} className="max-w-2xl text-balance text-[clamp(28px,3.4vw,36px)] leading-[1.1] tracking-[-0.02em]">
          {title}
        </h2>
        {note ? <p className="max-w-md text-[14px] leading-relaxed text-ink-2">{note}</p> : null}
      </div>
    </div>
  )
}

/** The quieter half of a heading: same weight, muted ink. */
export function Em({ children }: { children: ReactNode }) {
  return <span className="text-ink-3">{children}</span>
}

/** Mono label left, value right, dashed rule between rows. */
export function Register({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <dl className={className}>{children}</dl>
}

export function RegisterRow({ label, value }: { label: ReactNode; value: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-t border-dashed border-rule py-2.5 first:border-t-0">
      <dt className="mono-label shrink-0 text-ink-3">{label}</dt>
      <dd className="text-right text-[14px] text-ink">{value}</dd>
    </div>
  )
}

/** Two-digit index for steps and catalogue rows. */
export function pad(n: number): string {
  return String(n).padStart(2, '0')
}
